import { Component, inject, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { PsychologistService } from '../../services/psychologist.service';
import { Psychologist } from '../../models/psychologists';
import { MatIcon } from '@angular/material/icon';
import TYPES from '../../constants/types';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatRipple } from '@angular/material/core';
import { DATABASE_SERVICE, DatabaseService } from '../../services/database.service';
import { User } from '../../models/user';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EdgeFunctionCallerService } from '../../services/edge-function-caller.service';
import { ConfigService } from '../../services/config.service';

@Component({
  selector: 'app-approve-psychologist',
  imports: [
    MatIcon,
    TranslatePipe,
    MatRipple
],
  templateUrl: './approve-psychologist.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './approve-psychologist.component.scss'
})
export class ApprovePsychologistComponent {

  psychologistService: PsychologistService<Psychologist> = inject(PsychologistService);
  pendingPsychologists: WritableSignal<Psychologist[]> = signal<Psychologist[]>([]);
  edgeFunctionCallerService: EdgeFunctionCallerService = inject(EdgeFunctionCallerService)
  userService: DatabaseService<User> = inject(DATABASE_SERVICE);
  emailService: EdgeFunctionCallerService = inject(EdgeFunctionCallerService);
  configService: ConfigService = inject(ConfigService);
  translate: TranslateService = inject(TranslateService);
  snackBar: MatSnackBar = inject(MatSnackBar);

  loading: WritableSignal<boolean> = signal<boolean>(false);

  COUNTRIES: any;

  constructor(){

    this.COUNTRIES = TYPES.APPLICATION.COUNTRIES;

    this.loadingPendingApprovals();
        
  }

  loadingPendingApprovals(){
    this.loading.set(true);
    this.psychologistService.getAllPendingPsychologists().then(
      (data) => {
        this.pendingPsychologists.set(data);
        this.loading.set(false);
      }
    );
  }

  getCountryLabel(countryCode: String){
    for(let country of this.COUNTRIES){
      if(country.code === countryCode){
        return country.name;
      }
    }
  }

  async approve(psychologist: Psychologist){

    this.loading.set(true);

    const psychologistUpdated: Partial<Psychologist> = {
      ...psychologist,
      status: TYPES.DATABASE.STATUS.ACTIVE.code
    }

    const psychologistApproved = await this.psychologistService.update(
      TYPES.DATABASE.TABLES.PSYCHOLOGISTS,
      psychologist.id,
      psychologistUpdated
    );

    if(psychologistApproved){
      const userId = psychologist.user_id;
      if(userId){
        const userDB = await this.userService.getById(TYPES.DATABASE.TABLES.USER, userId);

        const user: Partial<User> = {
          ...userDB,
          profile: TYPES.DATABASE.PROFILES.PSYCHOLOGIST.key
        }

        await this.userService.update(
          TYPES.DATABASE.TABLES.USER,
          userId,
          user
        );

        await this.edgeFunctionCallerService.updateProfileToPsychologist(userId);

        const language: string = this.configService.preferedLanguage() ? this.configService.preferedLanguage() : 'en'
        await this.emailService.sendNotification({
          to: psychologist.email || '',
          template: TYPES.APPLICATION.EMAILS_TYPES.PROFILE_APPROVED,
          language: language,
          userName: '',
          psychologistName: psychologist.first_name || ''
        });

        const message:string = this.translate.instant('info.psychologist_approved');
        const action:string = this.translate.instant('buttons.dismiss');
        this.snackBar.open(message, action);

        this.loadingPendingApprovals();
      }
    }
  }

  async reject(psychologist: Psychologist){
    
    this.loading.set(true);
    
    await this.psychologistService.delete(
      TYPES.DATABASE.TABLES.PSYCHOLOGISTS,
      psychologist.id
    );

    const language: string = this.configService.preferedLanguage() ? this.configService.preferedLanguage() : 'en'
    await this.emailService.sendNotification({
      to: psychologist.email || '',
      template: TYPES.APPLICATION.EMAILS_TYPES.PROFILE_REJECTED,
      language: language,
      userName: '',
      psychologistName: psychologist.first_name || ''
    });

    const message:string = this.translate.instant('info.psychologist_rejected');
    const action:string = this.translate.instant('buttons.dismiss');
    this.snackBar.open(message, action);

    this.loadingPendingApprovals();

  }

  getLanguageLabel(languages: string[]){    
    let languagesString = '';
    for(const language of TYPES.LANGUAGES){
      if(languages.includes(language.code)){
        languagesString += language.name+', '
      }
    }
    return languagesString.slice(0, languagesString.length-2);
  }

}
